import React, { useState, useEffect, useMemo } from "react";
import {
Typography,
  Box,
  CircularProgress,
} from "@mui/material";

//constants
const CONSTANTS = {
  STOP_RADIUS: 50, // meters - radius to detect if vehicle is at a stop
  STOP_VISIT_TIME: 10, // seconds - time needed at a stop to mark it as visited
  DEPARTURE_DISTANCE: 30, // meters - distance from last stop to consider "departed"
  LOOK_AHEAD_DISTANCE: 1000, // meters - maximum distance to look ahead for future stops
  DATA_STALENESS_THRESHOLD: 30000,
  EARTH_RADIUS: 6371e3,
  MAX_STOP_DISTANCE: 800, // Maximum reasonable distance between stops (1km)
  MIN_STOP_DISTANCE: 100, // Minimum distance between stops (100m)
  AVG_SPEED: 35, // Average speed in km/h
  MIN_STOP_TIME: 10, // Minimum time between stops in seconds (10s)
  MAX_STOP_TIME: 100, // Maximum time between stops in seconds (15 min)
  TRAFFIC_MULTIPLIER: 1.1, // 10% buffer for traffic
};

const calculateRealisticETA = (distance) => {
  // Convert distance to km for speed calculation
  const distanceInKm = distance / 1000;

  // Calculate base time in seconds
  let baseTime = (distanceInKm / CONSTANTS.AVG_SPEED) * 3600;

  // Add small traffic buffer
  baseTime *= CONSTANTS.TRAFFIC_MULTIPLIER;

  // Add realistic stop light delays
  const numTrafficLights = Math.floor(distance / 800); // One light every 800m
  const avgTrafficLightDelay = 20; // 20 seconds per light
  baseTime += numTrafficLights * avgTrafficLightDelay;

  // Constrain to realistic minimum and maximum times
  return Math.min(
    Math.max(Math.round(baseTime), CONSTANTS.MIN_STOP_TIME),
    CONSTANTS.MAX_STOP_TIME
  );
};

const calculateImprovedDistance = (pos1, pos2) => {
  if (!pos1?.lat || !pos1?.lng || !pos2?.latitude || !pos2?.longitude)
    return Infinity;

  try {
    const φ1 = (pos1.lat * Math.PI) / 180;
    const φ2 = (parseFloat(pos2.latitude) * Math.PI) / 180;
    const Δφ = ((parseFloat(pos2.latitude) - pos1.lat) * Math.PI) / 180;
    const Δλ = ((parseFloat(pos2.longitude) - pos1.lng) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = CONSTANTS.EARTH_RADIUS * c;

    // Constrain to realistic values
    return Math.min(
      Math.max(distance, CONSTANTS.MIN_STOP_DISTANCE),
      CONSTANTS.MAX_STOP_DISTANCE
    );
  } catch (error) {
    console.error("Error calculating distance:", error);
    return Infinity;
  }
};

const formatTime = (timeString) => {
  if (!timeString) return "---";

  // If it's already in the format we want, return it
  if (
    typeof timeString === "string" &&
    (timeString.includes("m") || timeString.includes("s"))
  ) {
    return timeString;
  }

  // Convert to number if it's a string
  const seconds =
    typeof timeString === "string" ? parseInt(timeString) : timeString;
  if (isNaN(seconds) || seconds <= 0) return "---";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

const NextStopTableScreenScreen = ({
  details,
  stops = [],
  stopDetails = {},
  isInbound,
  currentPosition,
  routeCoordinates = [],
}) => {
  const [visitedStops, setVisitedStops] = useState(new Set());
  const [stopVisitTimer, setStopVisitTimer] = useState(null);
  const [currentStopId, setCurrentStopId] = useState(null);
  const [previousData, setPreviousData] = useState({
    nextStop: null,
    stopDetail: null,
    lastUpdateTime: null,
  });
  const [lastStopDistance, setLastStopDistance] = useState(null);
  const [lastVisitedStop, setLastVisitedStop] = useState(null);

  const getBestMetrics = (
    calculatedDistance,
    backendDistance,
    calculatedETA,
    backendETA
  ) => {
    let finalDistance = calculatedDistance;
    let finalETA = calculatedETA;

    // Convert backend distance to meters if it's in km
    if (backendDistance) {
      const backendInMeters =
        backendDistance > 100 ? backendDistance : backendDistance * 1000;

      // Take the smaller value if both exist and the difference isn't too large
      if (calculatedDistance && backendInMeters) {
        const diff = Math.abs(calculatedDistance - backendInMeters);
        if (diff / calculatedDistance < 0.5) {
          // If difference is less than 50%
          finalDistance = Math.min(calculatedDistance, backendInMeters);
        } else {
          finalDistance = calculatedDistance; // Use calculated if difference is too large
        }
      }
    }

    // Compare ETAs
    if (backendETA && calculatedETA) {
      const diff = Math.abs(calculatedETA - backendETA);
      if (diff / calculatedETA < 0.5) {
        // If difference is less than 50%
        finalETA = Math.min(calculatedETA, backendETA);
      } else {
        finalETA = calculatedETA; // Use calculated if difference is too large
      }
    }

    return {
      distance: finalDistance / 1000,
      eta: finalETA,
    };
  };

  // Calculate distance between two points
  const calculateDistance = (pos1, pos2) => {
    if (!pos1?.lat || !pos1?.lng || !pos2?.latitude || !pos2?.longitude)
      return Infinity;

    try {
      const φ1 = (pos1.lat * Math.PI) / 180;
      const φ2 = (parseFloat(pos2.latitude) * Math.PI) / 180;
      const Δφ = ((parseFloat(pos2.latitude) - pos1.lat) * Math.PI) / 180;
      const Δλ = ((parseFloat(pos2.longitude) - pos1.lng) * Math.PI) / 180;

      const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return CONSTANTS.EARTH_RADIUS * c;
    } catch (error) {
      console.error("Error calculating distance:", error);
      return Infinity;
    }
  };

  // Find route index for position
  const findRouteIndex = (position) => {
    if (!position || !routeCoordinates?.length) return -1;

    let minDistance = Infinity;
    let closestIndex = -1;

    routeCoordinates.forEach((coord, index) => {
      const distance = calculateDistance(position, {
        latitude: coord.latitude,
        longitude: coord.longitude,
      });

      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  };

  // Reset states when direction changes
  useEffect(() => {
    setVisitedStops(new Set());
    setStopVisitTimer(null);
    setCurrentStopId(null);
    setPreviousData({
      nextStop: null,
      stopDetail: null,
      lastUpdateTime: null,
    });
  }, [isInbound]);

  useEffect(()=>{
    console.log(stops, "nexttabeseenne")
  },[])

  // Determine next stop and match with current details
  const { nextStop, stopDetail } = useMemo(() => {
    try {
      if (!currentPosition || !stops?.length || !routeCoordinates?.length) {
        return previousData;
      }

      const currentIndex = findRouteIndex(currentPosition);
      if (currentIndex === -1) return previousData;

      // Check distance from last visited stop
      let distanceFromLastStop = Infinity;
      if (lastVisitedStop) {
        distanceFromLastStop = calculateImprovedDistance(
          currentPosition,
          lastVisitedStop
        );
        setLastStopDistance(distanceFromLastStop);
      }

      if (distanceFromLastStop < CONSTANTS.DEPARTURE_DISTANCE) {
        return previousData;
      }

      // Find all unvisited stops with improved distance calculation
      const stopsWithData = stops
        .filter((stop) => !visitedStops.has(stop.stopId))
        .map((stop) => ({
          ...stop,
          routeIndex: findRouteIndex({
            lat: parseFloat(stop.latitude),
            lng: parseFloat(stop.longitude),
          }),
          distance: calculateImprovedDistance(currentPosition, stop),
        }))
        .filter((stop) => stop.routeIndex !== -1);

      const validStops = stopsWithData
        .filter((stop) =>
          isInbound
            ? stop.routeIndex <= currentIndex
            : stop.routeIndex >= currentIndex
        )
        .sort((a, b) =>
          isInbound ? b.routeIndex - a.routeIndex : a.routeIndex - b.routeIndex
        );

      if (!validStops.length) return previousData;

      const nextValidStop = validStops[0];
      const backendStopDetail = stopDetails[nextValidStop.stopId];

      // Calculate frontend metrics
      const calculatedDistance = nextValidStop.distance;
      const calculatedETA = calculateRealisticETA(calculatedDistance);

      // Get best metrics by comparing backend and frontend calculations
      const bestMetrics = getBestMetrics(
        calculatedDistance,
        backendStopDetail?.distance,
        calculatedETA,
        backendStopDetail?.eta
      );

      // Return combined metrics
      return {
        nextStop: nextValidStop,
        stopDetail: {
          ...backendStopDetail,
          distance: bestMetrics.distance,
          eta: bestMetrics.eta,
        },
      };
    } catch (error) {
      console.error("Error determining next stop:", error);
      return previousData;
    }
  }, [
    currentPosition,
    stops,
    stopDetails,
    routeCoordinates,
    isInbound,
    visitedStops,
    lastVisitedStop,
    lastStopDistance,
  ]);

  // Update previous data when we get valid new data
  useEffect(() => {
    if (nextStop && stopDetail) {
      setPreviousData({
        nextStop,
        stopDetail,
        lastUpdateTime: Date.now(),
      });
    }
  }, [nextStop, stopDetail]);

  // Track stop visits
  useEffect(() => {
    if (!currentPosition || !stops?.length) return;

    stops.forEach((stop) => {
      const distance = calculateDistance(currentPosition, stop);
      if (distance <= CONSTANTS.STOP_RADIUS) {
        if (!stopVisitTimer && !visitedStops.has(stop.stopId)) {
          setStopVisitTimer({
            stopId: stop.stopId,
            startTime: Date.now(),
          });
        } else if (stopVisitTimer?.stopId === stop.stopId) {
          const timeSpent = (Date.now() - stopVisitTimer.startTime) / 1000;
          if (timeSpent >= CONSTANTS.STOP_VISIT_TIME) {
            setVisitedStops((prev) => new Set([...prev, stop.stopId]));
            setStopVisitTimer(null);
          }
        }
      } else if (stopVisitTimer?.stopId === stop.stopId) {
        setStopVisitTimer(null);
      }
    });
  }, [currentPosition, stops, visitedStops, stopVisitTimer]);

  // Determine if data is stale
  const isDataStale = useMemo(() => {
    if (!nextStop || !stopDetail) return true;
    if (!previousData.lastUpdateTime) return false;
    return (
      Date.now() - previousData.lastUpdateTime >
      CONSTANTS.DATA_STALENESS_THRESHOLD
    );
  }, [nextStop, stopDetail, previousData.lastUpdateTime]);

  const distance = nextStop && currentPosition
  ? (calculateImprovedDistance(currentPosition, nextStop) / 1000).toFixed(1)
  : null;

const eta = nextStop && currentPosition
  ? formatTime(calculateRealisticETA(calculateImprovedDistance(currentPosition, nextStop)))
  : null;

  return (
    <Box 
      sx={{
        width: '33vw',
        height: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#183059', 
        color: '#F9E719',
        padding: '0.5rem',
        textAlign: 'center'
      }}
    >
      {/* Next Stop Section */}
      <Box sx={{
        backgroundColor: 'rgba(32, 168, 224, 0.2)',
        borderRadius: '16px',
        padding: '0.5rem',
        width: '95%',
        marginBottom: '1rem'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', flexDirection:"column" }}>
          <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold', marginRight: '0.5rem' }}>
            Next Stop
          </Typography>
          <Typography variant="h1" sx={{ color: '#F9E719', fontWeight: 'bold' }}>
            {nextStop?.name || "Determining Next Stop"}
          </Typography>
        </Box>
      </Box>
      
      {/* Distance and ETA */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-around',
        width: '90%',
        gap: '1rem',
        flexDirection: "column"
      }}>
        {/* ETA */}
        <Box sx={{
          backgroundColor: 'rgba(32, 168, 224, 0.2)',
          borderRadius: '16px',
          padding: '1rem',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            ETA
          </Typography>
          <Typography variant="h2" sx={{ color: '#F9E719', fontWeight: 'bold' }}>
            {eta || <CircularProgress color="inherit" />}
          </Typography>
        </Box>
        {/* Distance */}
        <Box sx={{
          backgroundColor: 'rgba(32, 168, 224, 0.2)',
          borderRadius: '16px',
          padding: '1rem',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Distance
          </Typography>
          <Typography variant="h2" sx={{ color: '#F9E719', fontWeight: 'bold' }}>
            {distance ? `${distance} km` : <CircularProgress color="inherit" />}
          </Typography>
        </Box>

      </Box>
    </Box>
  );
};

export default NextStopTableScreenScreen;

